import { ConnectError, Code, type Interceptor } from '@connectrpc/connect'
import { createConnectTransport } from '@connectrpc/connect-web'
import { toast } from 'sonner'
import { authStore } from '../stores/auth.store'
import { supabase } from './supabase'
import { Sentry } from './sentry'
import { reportError } from './error-reporting'
import {
  newRequestId,
  recordResponseCorrelation,
} from './observability'

const authInterceptor: Interceptor = (next) => async (req) => {
  const { session } = authStore.state
  if (session?.access_token) {
    req.header.set('Authorization', `Bearer ${session.access_token}`)
  }
  const clientRequestId = newRequestId()
  req.header.set('X-Request-ID', clientRequestId)
  const rpcName = `${req.service.typeName}/${req.method.name}`

  return Sentry.startSpan(
    {
      name: rpcName,
      op: 'rpc.client',
      attributes: {
        'rpc.system': 'connect',
        'rpc.service': req.service.typeName,
        'rpc.method': req.method.name,
        'http.request_id': clientRequestId,
      },
    },
    async (span) => {
      try {
        const response = await next(req)
        recordResponseCorrelation(response.header, span)
        span.setStatus({ code: 1 })
        return response
      } catch (error) {
        const connectError = ConnectError.from(error)
        const backendRequestId =
          connectError.metadata?.get('X-Request-ID') || clientRequestId
        const backendTraceId = connectError.metadata?.get('X-Trace-ID')
        span.setAttribute('server.request_id', backendRequestId)
        if (backendTraceId) {
          span.setAttribute('sentry.trace_id', backendTraceId)
        }
        span.setAttribute('rpc.connect_rpc.error_code', connectError.code)
        span.setStatus({
          code: 2,
          message:
            connectError.code === Code.Canceled
              ? 'cancelled'
              : 'internal_error',
        })

        if (
          connectError.code === Code.Unauthenticated
        ) {
          await supabase.auth.signOut()
          toast.error('Session expired. Please log in again.')
        } else if (
          !(
            connectError.code === Code.Unknown &&
            /Failed to fetch|NetworkError|Load failed/i.test(
              connectError.rawMessage
            )
          )
        ) {
          reportError(error, {
            feature: 'api',
            apiService: req.service.typeName,
            apiMethod: req.method.name,
            backendRequestId,
            backendTraceId,
          })
        }
        throw error
      }
    }
  )
}

export const connectTransport = createConnectTransport({
  baseUrl: import.meta.env.VITE_API_URL || 'http://localhost:8080',
  interceptors: [authInterceptor],
})
